
export type UserRole = 'vendor' | 'customer' | 'internal' | 'anonymous';


export interface UserIdentity {
  distinctId: string;
  role: UserRole;
  properties?: UserProperties;
}

export interface UserProperties {
  email?: string;
  name?: string;
  phone?: string;
  city?: string;

  contributor_code?: string;
  contributor_id?: string;
  account_type?: 'individual' | 'agency';
  onboarding_stage?: string;
  bio_done?: boolean;
  samples_done?: boolean;
  rate_done?: boolean;
  samples_count?: number;
  service_category?: string;
  verification_status?: string;
  ai_ready?: boolean;

  // Client-specific
  client_type?: 'enterprise' | 'msme';
  company_name?: string;
  company_size?: string;
  industry?: string;
  lifecycle_stage?: string;
  total_projects?: number;
  total_revenue?: number;
  customer_since?: string;

  // Free-form escape hatch — should almost never be used; prefer typed fields.
  [key: string]: unknown;
}

/** Event property bag. Providers may transform this (GA4 renames some keys
 *  because it reserves `page_title`, `page_location`, etc.). */
export type EventProperties = Record<string, EventPropertyValue>;

export type EventPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | number[];

/** Provider contract. Add a new analytics tool by writing another
 *  implementation and registering it in AnalyticsService. */
export interface AnalyticsProvider {
  /** Provider name — used in debug logs. */
  readonly name: string;
  /** True once the SDK has been loaded/configured. */
  isReady(): boolean;
  /** Load the SDK. Called once at app boot. Idempotent — must survive being
   *  called twice. Returning a Promise lets async loaders (script tag) resolve. */
  init(): Promise<void> | void;
  /** Attach identity + user properties. */
  identify(identity: UserIdentity): void;
  /** Update People / user properties without changing distinct_id. */
  setUserProperties(props: UserProperties): void;
  /** Fire an event. Providers should swallow errors internally so a
   *  single-provider failure doesn't affect the other. */
  track(event: string, properties?: EventProperties): void;
  /** Track a route change. Providers may map to their native pageview mechanism. */
  trackRoute(path: string, title?: string): void;
  /** Wipe the current user's identity. Called on logout BEFORE localStorage
   *  is cleared so both providers still see the outgoing distinctId. */
  reset(): void;
}
